import type { ReactNode } from "react";
import { cn } from "@core/utils";
import { Illustration, type IllustrationKind } from "./Illustration";

interface EmptyStateProps {
  /** One line, what is missing: « Aucun appel pour l'instant ». */
  title: string;
  description?: string;
  illustration?: IllustrationKind;
  /** Optional on purpose: an Administrateur's empty cohort list has no button to offer. */
  action?: ReactNode;
  /** Dashed border, the look of the empty cohort list (Figma 1032:1646). */
  dashed?: boolean;
  className?: string;
}

/**
 * Inline empty state: inside a list, a tab or a drawer. It is not `ResultCard`, which is the
 * full-page ending of a flow (accepted, expired, invalid); this one sits in a bordered card
 * among other content.
 */
export function EmptyState({
  title,
  description,
  illustration = "empty",
  action,
  dashed = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-sm rounded-lg border bg-surface-card px-lg py-3xl text-center",
        dashed
          ? "border-dashed border-(--border-field)"
          : "border-(--border-default)",
        className
      )}
    >
      <Illustration kind={illustration} />
      <div className="flex flex-col items-center gap-xxs">
        <p className="text-md-medium text-(--text-primary)">{title}</p>
        {description ? (
          <p className="max-w-80 text-sm-regular text-(--text-secondary)">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div className="pt-xs">{action}</div> : null}
    </div>
  );
}

export type { EmptyStateProps };
